import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useProject } from "./ProjectContext";
import { workbookCache } from "../../excel/workbook/cache";
import { QTO_SOURCE_ORDER, type QtoSourceId } from "../../integrations/qto/registry";
import { useQtoConnectedSources, useQtoSourcesEnabled } from "../hooks/useQtoSources";

export type EdiphiDatasourceType = "estimate" | "gcgr" | "contracting" | "cost-model";
/**
 * Take-off sources ARE the QTO sources. Aliased rather than re-declared so the two can't
 * drift — they previously did, with this union saying "autodesk" where QtoSourceId says "ato".
 */
export type TakeOffSourceType = QtoSourceId;
export type DatasourceKind = "ediphi" | "takeOff";

interface DatasourceContextType {
  ediphiDatasources: EdiphiDatasourceType[];
  takeOffSources: TakeOffSourceType[];
  hasDatasource: (kind: DatasourceKind, type: EdiphiDatasourceType | TakeOffSourceType) => boolean;
  addDatasource: (kind: DatasourceKind, type: EdiphiDatasourceType | TakeOffSourceType) => void;
  removeDatasource: (kind: DatasourceKind, type: EdiphiDatasourceType | TakeOffSourceType) => void;
  /**
   * Bumped when a load or refresh finishes. Cards read their figures from the workbook's saved card
   * summaries, so this is what tells them to read again.
   */
  dataVersion: number;
  markDataRefreshed: () => void;
}

const EDIPHI_TYPES: readonly EdiphiDatasourceType[] = ["estimate", "gcgr", "contracting", "cost-model"];
const TAKEOFF_TYPES: readonly TakeOffSourceType[] = QTO_SOURCE_ORDER;

const EDIPHI_ORDER_KEY = "ediphiDatasourceOrder";
const TAKEOFF_ORDER_KEY = "takeOffSourceOrder";

const DatasourceContext = createContext<DatasourceContextType | undefined>(undefined);

const parseCachedOrder = <T extends string>(cached: unknown, allowed: readonly T[]): T[] => {
  if (!Array.isArray(cached)) return [];
  const seen = new Set<T>();
  return cached.reduce<T[]>((acc, entry) => {
    if (typeof entry !== "string") return acc;
    const type = entry as T;
    if (!allowed.includes(type) || seen.has(type)) return acc;
    seen.add(type);
    acc.push(type);
    return acc;
  }, []);
};

const sortByOrder = <T extends string>(present: readonly T[], order: readonly T[]): T[] => {
  const recorded = order.filter((type) => present.includes(type));
  const unrecorded = present.filter((type) => !order.includes(type));
  return [...recorded, ...unrecorded];
};

export const DatasourceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedProject, selectedEstimate, selectedGcgr, isContractingLoaded, selectedCostModel } = useProject();
  const [dataVersion, setDataVersion] = useState<number>(0);
  const [ediphiOrder, setEdiphiOrder] = useState<EdiphiDatasourceType[]>([]);
  const [takeOffOrder, setTakeOffOrder] = useState<TakeOffSourceType[]>([]);

  const hasRestoredRef = useRef<boolean>(false);
  const lastProjectIdRef = useRef<string | null>(null);

  const presentEdiphiTypes = useMemo(
    () =>
      EDIPHI_TYPES.filter((type) => {
        switch (type) {
          case "estimate":
            return Boolean(selectedEstimate);
          // GCGR and cost model selections are project-scoped, so a selection left over from a
          // previous project is not present even before it is cleared.
          case "gcgr":
            return Boolean(selectedGcgr) && selectedGcgr?.project === selectedProject?.id;
          case "contracting":
            return isContractingLoaded;
          case "cost-model":
            return Boolean(selectedCostModel) && selectedCostModel?.project === selectedProject?.id;
          default:
            return false;
        }
      }),
    [selectedEstimate, selectedGcgr, isContractingLoaded, selectedCostModel, selectedProject?.id]
  );

  const connectedById = useQtoConnectedSources();
  const enabledById = useQtoSourcesEnabled();

  const presentTakeOffTypes = useMemo<TakeOffSourceType[]>(
    () => TAKEOFF_TYPES.filter((type) => enabledById[type] && connectedById[type]),
    [enabledById, connectedById]
  );

  const ediphiDatasources = useMemo(
    () => sortByOrder(presentEdiphiTypes, ediphiOrder),
    [presentEdiphiTypes, ediphiOrder]
  );
  const takeOffSources = useMemo(
    () => sortByOrder(presentTakeOffTypes, takeOffOrder),
    [presentTakeOffTypes, takeOffOrder]
  );

  useEffect(() => {
    if (hasRestoredRef.current) return;

    let isCurrent = true;

    const restore = async () => {
      const [ediphi, takeOff] = await Promise.all([
        workbookCache.get(EDIPHI_ORDER_KEY),
        workbookCache.get(TAKEOFF_ORDER_KEY),
      ]);
      if (!isCurrent) return;

      if (ediphi !== null) setEdiphiOrder(parseCachedOrder(ediphi, EDIPHI_TYPES));
      if (takeOff !== null) setTakeOffOrder(parseCachedOrder(takeOff, TAKEOFF_TYPES));
      hasRestoredRef.current = true;
    };

    restore();

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    const projectId = selectedProject?.id ?? null;
    const previousProjectId = lastProjectIdRef.current;
    lastProjectIdRef.current = projectId;

    if (previousProjectId === null || previousProjectId === projectId) return;

    setEdiphiOrder([]);
    setTakeOffOrder([]);
    void workbookCache.delete(EDIPHI_ORDER_KEY);
    void workbookCache.delete(TAKEOFF_ORDER_KEY);
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!hasRestoredRef.current) return;
    void workbookCache.set({ [EDIPHI_ORDER_KEY]: ediphiOrder });
  }, [ediphiOrder]);

  useEffect(() => {
    if (!hasRestoredRef.current) return;
    void workbookCache.set({ [TAKEOFF_ORDER_KEY]: takeOffOrder });
  }, [takeOffOrder]);

  const hasDatasource = useCallback(
    (kind: DatasourceKind, type: EdiphiDatasourceType | TakeOffSourceType) => {
      const present: readonly string[] = kind === "ediphi" ? presentEdiphiTypes : presentTakeOffTypes;
      return present.includes(type);
    },
    [presentEdiphiTypes, presentTakeOffTypes]
  );

  const markDataRefreshed = useCallback(() => {
    setDataVersion((current) => current + 1);
  }, []);

  const addDatasource = useCallback((kind: DatasourceKind, type: EdiphiDatasourceType | TakeOffSourceType) => {
    const append = <T extends string>(current: T[]) =>
      current.includes(type as T) ? current : [...current, type as T];

    if (kind === "ediphi") {
      setEdiphiOrder((current) => append(current));
    } else {
      setTakeOffOrder((current) => append(current));
    }
  }, []);

  const removeDatasource = useCallback((kind: DatasourceKind, type: EdiphiDatasourceType | TakeOffSourceType) => {
    const drop = <T extends string>(current: T[]) => current.filter((entry) => entry !== type);

    if (kind === "ediphi") {
      setEdiphiOrder((current) => drop(current));
    } else {
      setTakeOffOrder((current) => drop(current));
    }
  }, []);

  return (
    <DatasourceContext.Provider
      value={{
        ediphiDatasources,
        takeOffSources,
        hasDatasource,
        addDatasource,
        removeDatasource,
        dataVersion,
        markDataRefreshed,
      }}
    >
      {children}
    </DatasourceContext.Provider>
  );
};

export const useDatasources = (): DatasourceContextType => {
  const context = useContext(DatasourceContext);
  if (!context) {
    throw new Error("useDatasources must be used within a DatasourceProvider");
  }
  return context;
};
